import { useParams, Link } from 'react-router-dom';
import { allArticles } from '@/mocks/articlesData';

const siteUrl = 'https://superbuyspreadsheet.best';

function buildArticleJsonLd(article: typeof allArticles[0]) {
  const articleUrl = `${siteUrl}/article/${article.slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Organization',
      name: article.author,
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png',
      },
    },
    datePublished: article.lastUpdated,
    dateModified: article.lastUpdated,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    image: article.coverImage,
    inLanguage: 'en',
  };
}

function buildBreadcrumbJsonLd(article: typeof allArticles[0]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: `${siteUrl}/article/${article.slug}`,
      },
    ],
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
          <p className="text-foreground-500 mb-6">The article you are looking for does not exist or may have been moved.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Back to Homepage
          </Link>
        </div>
      </main>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildArticleJsonLd(article)),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildBreadcrumbJsonLd(article)),
        }}
      />

      <main>
        <article className="min-h-screen bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6 pt-8 pb-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Superbuy Spreadsheet Home
            </Link>
          </div>

          <header className="relative w-full py-16 md:py-24 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">
                  {article.readingTime}
                </span>
                <span className="text-xs text-foreground-400">
                  Updated: {article.lastUpdated}
                </span>
              </div>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-tight mb-6">
                {article.title}
              </h1>
              <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl">
                {article.description}
              </p>
            </div>
          </header>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full aspect-[2/1] object-cover object-top"
                loading="lazy"
              />
            </div>

            <div className="prose-custom">
              {article.content.map((block, idx) => {
                if (block.type === 'heading' && block.level === 2) {
                  return (
                    <h2
                      key={idx}
                      className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-5 leading-tight"
                    >
                      {block.content}
                    </h2>
                  );
                }
                if (block.type === 'paragraph') {
                  return (
                    <p
                      key={idx}
                      className="text-base text-foreground-600 leading-relaxed mb-5"
                    >
                      {block.content}
                    </p>
                  );
                }
                return null;
              })}
            </div>

            <div className="mt-16 p-8 rounded-2xl bg-primary-50 border border-primary-100">
              <p className="text-base font-semibold text-primary-800 mb-3">
                Ready to start shopping?
              </p>
              <p className="text-sm text-primary-700 mb-5 leading-relaxed">
                Browse our verified product categories and discover quality items curated by the community. Every product is QC-checked and linked directly for your convenience.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Browse Categories
                  <i className="ri-arrow-right-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary-300 text-primary-700 text-sm font-medium hover:bg-primary-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                </Link>
              </div>
            </div>

            <nav className="mt-12 pt-8 border-t border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-400 uppercase tracking-wider mb-4">
                Explore More Guides
              </h3>
              <div className="flex flex-wrap gap-2 mb-6">
                {allArticles
                  .filter((a) => a.slug !== slug)
                  .slice(0, 6)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="inline-flex items-center px-4 py-2 rounded-full bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:text-foreground-900 hover:border-background-300/60 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {a.category}: {a.title.length > 50 ? a.title.slice(0, 50) + '...' : a.title}
                    </Link>
                  ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href="https://kaspreadsheet.com/category-911-Shoes.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center px-4 py-2 rounded-full bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:text-foreground-900 hover:border-background-300/60 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Browse Shoes
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center px-4 py-2 rounded-full bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:text-foreground-900 hover:border-background-300/60 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Visit Main Store
                </a>
              </div>
            </nav>

            <footer className="mt-16 pt-8 border-t border-background-200/70 text-center">
              <Link
                to="/"
                className="text-sm text-foreground-400 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
              >
                &larr; Return to Superbuy Spreadsheet Homepage
              </Link>
              <p className="text-xs text-foreground-300 mt-3">
                &copy; {new Date().getFullYear()} Superbuy Spreadsheet. All rights reserved.
              </p>
            </footer>
          </div>
        </article>
      </main>
    </>
  );
}